import Icon from '@/components/ui/AppIcon';

interface RegionalOffice {
  region: string;
  city: string;
  address: string;
  coordinates: { lat: number; lng: number };
  timezone: string;
  businessHours: string;
  languages: string[];
  primaryContact: string;
}

const RegionalOffices = () => {
  const offices: RegionalOffice[] = [
    {
      region: 'Hong Kong Headquarters',
      city: 'Hong Kong',
      address: 'S022, 2/F The Capital, 61–65 Chatham Road South, Tsim Sha Tsui, Hong Kong',
      coordinates: { lat: 22.2793, lng: 114.1628 },
      timezone: 'GMT+8 (HKT)',
      businessHours: 'Monday-Friday, 9:00-18:00',
      languages: ['English', 'Cantonese', 'Mandarin'],
      primaryContact: 'hq@dannyclans.com'
    },
    {
      region: 'India Operations',
      city: 'Hisar',
      address: 'Office Number 8, TC Tower, DN College Rd, Aggarwal Colony, Hisar, Haryana 125001, India',
      coordinates: { lat: 29.1492, lng: 75.7217 },
      timezone: 'GMT+5:30 (IST)',
      businessHours: 'Monday-Friday, 9:00-18:00',
      languages: ['English', 'Hindi'],
      primaryContact: 'india@dannyclans.com'
    }
  ];

  return (
    <section className="bg-surface border border-border rounded-sm p-8">
      <div className="flex items-center space-x-3 mb-8">
        <Icon name="GlobeAltIcon" size={28} variant="solid" className="text-primary" />
        <h2 className="font-headline font-bold text-2xl text-primary">
          Global Office Network
        </h2>
      </div>
      
      <p className="font-body text-base text-text-secondary mb-8 leading-relaxed">
        Regional presence supporting 24/5 global operations. All offices maintain Hong Kong regulatory standards while providing localized support.
      </p>
      
      <div className="space-y-8">
        {offices.map((office, index) => (
          <div key={index} className="border border-border rounded-sm overflow-hidden">
            <div className="p-6 bg-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-headline font-bold text-lg text-primary mb-1">
                    {office.region}
                  </h3>
                  <p className="font-body text-base text-text-secondary">
                    {office.city}
                  </p>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1 bg-success bg-opacity-10 rounded-sm">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                  <span className="font-accent text-xs text-success">Operational</span>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <Icon name="MapPinIcon" size={18} variant="outline" className="text-accent mt-1" />
                  <p className="font-body text-sm text-text-secondary">
                    {office.address}
                  </p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Icon name="ClockIcon" size={18} variant="outline" className="text-accent" />
                  <p className="font-body text-sm text-text-secondary">
                    {office.businessHours} ({office.timezone})
                  </p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Icon name="LanguageIcon" size={18} variant="outline" className="text-accent" />
                  <p className="font-body text-sm text-text-secondary">
                    {office.languages.join(', ')}
                  </p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Icon name="EnvelopeIcon" size={18} variant="outline" className="text-accent" />
                  <a 
                    href={`mailto:${office.primaryContact}`}
                    className="font-body text-sm text-accent hover:text-primary transition-colors duration-smooth"
                  >
                    {office.primaryContact}
                  </a>
                </div>
              </div>
            </div>
            
            <div className="w-full h-64 bg-muted relative overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title={`${office.city} Office Location`}
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${office.coordinates.lat},${office.coordinates.lng}&z=15&output=embed`}
                className="border-0"
              />
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-8 p-6 bg-accent bg-opacity-5 border border-accent rounded-sm">
        <div className="flex items-start space-x-3">
          <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-accent mt-1" />
          <div>
            <h4 className="font-headline font-semibold text-sm text-primary mb-2">
              Regional Support Coverage
            </h4>
            <p className="font-body text-sm text-text-secondary leading-relaxed">
              While all offices provide full service capabilities, inquiries are routed to Hong Kong headquarters for regulatory compliance verification. Regional offices handle technical integration and ongoing account support.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegionalOffices;